import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff, Mail, Lock, User } from 'lucide-react';
import { BackButton } from '@/components/ui/BackButton';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';

interface AuthPageProps {
  onBack: () => void;
  onSuccess: () => void;
  onOpenPrivacy?: () => void;
  onOpenTerms?: () => void;
}

type AuthMode = 'signin' | 'signup' | 'reset';

export const AuthPage = ({ onBack, onSuccess, onOpenPrivacy, onOpenTerms }: AuthPageProps) => {
  const { signIn, signUp, resetPassword, continueAsGuest } = useAuth();
  const { toast } = useToast();
  const [mode, setMode] = useState<AuthMode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  const [guestEmail, setGuestEmail] = useState('');
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (mode === 'signin') {
        const { error } = await signIn(email, password);
        if (error) {
          toast({
            title: 'Sign in failed',
            description: error.message,
            variant: 'destructive'
          });
        } else {
          toast({ title: 'Welcome back!' });
          onSuccess();
        }
      } else if (mode === 'signup') {
        const { error } = await signUp(email, password, name);
        if (error) {
          toast({
            title: 'Sign up failed',
            description: error.message,
            variant: 'destructive'
          });
        } else {
          toast({
            title: 'Account created',
            description: 'You can now start your journey.'
          });
          onSuccess();
        }
      } else if (mode === 'reset') {
        const { error } = await resetPassword(email);
        if (error) {
          toast({
            title: 'Reset failed',
            description: error.message,
            variant: 'destructive'
          });
        } else {
          toast({
            title: 'Check your email',
            description: 'We sent you a password reset link.'
          });
          setMode('signin');
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailContinue = () => {
    if (!guestEmail || !guestEmail.includes('@')) {
      toast({
        title: 'Please enter a valid email',
        variant: 'destructive'
      });
      return;
    }
    // Store email for future use, continue as guest with email captured
    localStorage.setItem('guestEmail', guestEmail);
    continueAsGuest();
    toast({
      title: 'Welcome to Layālī',
      description: 'Your progress will be saved with your email.'
    });
    onSuccess();
  };

  return (
    <div className="min-h-screen bg-paper">
      <header className="border-b border-border/50">
        <div className="max-w-3xl mx-auto px-6 py-4 flex justify-between items-center">
          <BackButton onClick={onBack} label="Back" srLabel="Return to previous page" />
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
            {mode === 'signin' ? 'Sign In' : mode === 'signup' ? 'Create Account' : 'Reset Password'}
          </span>
        </div>
      </header>

      <main className="max-w-md mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="text-center mb-10">
            <div className="text-4xl mb-4" style={{ color: 'hsl(38, 40%, 55%)' }}>☽</div>
            <h1 className="font-display text-3xl text-ink mb-2">
              {mode === 'signin' ? 'Welcome Back' : mode === 'signup' ? 'Join the Journey' : 'Reset Password'}
            </h1>
            <p className="text-muted-foreground">
              {mode === 'signin' 
                ? 'Continue your spiritual journey' 
                : mode === 'signup' 
                  ? 'Save your progress across devices'
                  : 'We\'ll send you a reset link'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {mode === 'signup' && (
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm">Name (optional)</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    className="pl-10"
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="pl-10"
                />
              </div>
            </div>

            {mode !== 'reset' && (
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    minLength={6}
                    className="pl-10 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            )}

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? 'Please wait...' : 
                mode === 'signin' ? 'Sign In' : 
                mode === 'signup' ? 'Create Account' : 
                'Send Reset Link'}
            </Button>
          </form>

          <div className="mt-6 space-y-4">
            {mode === 'signin' && (
              <>
                <button
                  onClick={() => setMode('reset')}
                  className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Forgot your password?
                </button>
                <div className="text-center text-sm text-muted-foreground">
                  Don't have an account?{' '}
                  <button 
                    onClick={() => setMode('signup')} 
                    className="text-gold hover:underline"
                  >
                    Sign up
                  </button>
                </div>
              </>
            )}

            {mode === 'signup' && (
              <div className="text-center text-sm text-muted-foreground">
                Already have an account?{' '}
                <button 
                  onClick={() => setMode('signin')} 
                  className="text-gold hover:underline"
                >
                  Sign in
                </button>
              </div>
            )}

            {mode === 'reset' && (
              <button
                onClick={() => setMode('signin')}
                className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Back to sign in
              </button>
            )}
          </div>

          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border/50"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-paper px-2 text-muted-foreground">Or</span>
            </div>
          </div>

          <button
            onClick={() => setShowEmailCapture(!showEmailCapture)}
            className="w-full text-sm tracking-wide text-muted-foreground/70 hover:text-muted-foreground transition-colors duration-300"
          >
            Continue with Email
            <span className="block text-xs mt-0.5 opacity-70">No account required</span>
          </button>

          <AnimatePresence>
            {showEmailCapture && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.4, ease: 'easeInOut' }}
                className="overflow-hidden"
              >
                <div className="pt-4 space-y-3">
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/50" />
                    <Input
                      type="email"
                      value={guestEmail}
                      onChange={(e) => setGuestEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="pl-10 border-border/30 bg-transparent focus:border-border/60"
                      aria-label="Email for saving progress"
                    />
                  </div>
                  <p className="text-xs text-center text-muted-foreground/60 leading-relaxed">
                    No password needed — just your email.<br />
                    We'll save your progress so you can continue later.
                  </p>
                  {(onOpenPrivacy || onOpenTerms) && (
                    <p className="text-[10px] text-center text-muted-foreground/40 leading-relaxed">
                      By continuing you agree to our{' '}
                      {onOpenTerms && <button onClick={onOpenTerms} className="underline underline-offset-2 hover:text-muted-foreground/60 transition-colors">Terms</button>}
                      {onOpenTerms && onOpenPrivacy && ' & '}
                      {onOpenPrivacy && <button onClick={onOpenPrivacy} className="underline underline-offset-2 hover:text-muted-foreground/60 transition-colors">Privacy Policy</button>}
                    </p>
                  )}
                  <Button
                    variant="outline"
                    onClick={handleEmailContinue}
                    className="w-full border-border/30 text-muted-foreground hover:text-foreground hover:border-border/50 transition-all duration-300"
                  >
                    Continue
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </main>
    </div>
  );
};
